import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Zap } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/@/components/ui/tabs";
import ModalAlerta from 'src/components/modal/ModalAlerta';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import ModalImportarJSON from 'src/components/modal/ModalImportarJSON';
import { downloadJSON, buildEnvelope } from 'src/utils/exportImport.utils';

// actions
import v2_actionDeletarPipeline from 'src/actions/v2/crm/v2_actionDeletarPipeline';
import v2_actionConfigCrm from 'src/actions/v2/crm/v2_actionConfigCrm';
import v2_actionCriarPipeline from 'src/actions/v2/cliente/v2_actionCriarPipeline';
import v2_actionCriarEstagioFunil from 'src/actions/v2/cliente/v2_actionCriarEstagioFunil';
import v2_actionAddCrmSource from 'src/actions/v2/crm/v2_actionAddCrmSource';
import v2_actionEditarCrmSource from 'src/actions/v2/crm/v2_actionEditarCrmSource';
import v2_actionDeletarCrmSource from 'src/actions/v2/crm/v2_actionDeletarCrmSource';
import v2_actionEditCrmReason from 'src/actions/v2/crm/v2_actionEditCrmReason';
import v2_actionAddCrmReason from 'src/actions/v2/crm/v2_actionAddCrmReason';
import v2_actionListScoringDefinitions from 'src/actions/v2/crm/v2_actionListScoringDefinitions';
import v2_actionAddScoringDefinition from 'src/actions/v2/crm/v2_actionAddScoringDefinition';
import v2_actionEditScoringDefinition from 'src/actions/v2/crm/v2_actionEditScoringDefinition';
import v2_actionDeleteScoringDefinition from 'src/actions/v2/crm/v2_actionDeleteScoringDefinition';

// local components
import PipileneItem from './PipileneItem';
import DrawerNovoFunil from './DrawerNovoFunil';
import TableReasons from './TableReasons';
import TableOrigins from './TableOrigins';
import ModalReasons from './ModalReasons';
import ModalOrigins from './ModalOrigins';
import TableScoring from './TableScoring';
import ModalScoring from './ModalScoring';
import TabCustomFields from './TabCustomFields';
import TabLayoutConfig from './TabLayoutConfig';
import TabLeadTags from './TabLeadTags';
import TabLeadStatus from './TabLeadStatus';

// design system
import { FUNIL_DESIGN as D } from './FunilComponent.design';

// Aceita só nomes de aba conhecidos: `?tab=` vem da URL e não deve conseguir
// colocar a página num estado que não existe.
const TABS_VALIDAS = ['kanban', 'perdas', 'origens', 'lead-tags', 'lead-status', 'campos', 'layout'];

export default function FunilComponent({ cliente, user }) {
  const { t } = useTranslation('settings');
  const [searchParams] = useSearchParams();
  const tabInicial = searchParams.get('tab');
  const [modalAlerta, setModalAlerta] = useState(false);
  const [openDrawerNovoFunil, setOpenDrawerNovoFunil] = useState(false);
  const [configCrm, setConfigCrm] = useState(null);
  const [openModalConfirmacaoExclusao, setOpenModalConfirmacaoExclusao] = useState(false);
  const [openModalImportarFunil, setOpenModalImportarFunil] = useState(false);

  const [pipeline, setPipeline] = useState(null);
  const [pipelineId, setPipelineId] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [motivos, setMotivos] = useState([]);
  const [origens, setOrigens] = useState([]);

  //reasons
  const [openConfirmReasonDelete, setOpenConfirmReasonDelete] = useState(false);
  const [openDialogReason, setOpenDialogReason] = useState(false);
  const [idReason, setIdReason] = useState(null);
  const [action, setAction] = useState(null);
  const [nameReason, setNameReason] = useState(null);

  //origins
  const [openConfirmOriginDelete, setOpenConfirmOriginDelete] = useState(false);
  const [openDialogOrigin, setOpenDialogOrigin] = useState(false);
  const [idOrigin, setIdOrigin] = useState(null);
  const [actionOrigin, setActionOrigin] = useState(null);
  const [nameOrigin, setNameOrigin] = useState(null);
  const [descricaoOrigin, setDescricaoOrigin] = useState(null);
  const [ativoOrigin, setAtivoOrigin] = useState(true);

  //scoring
  const [scoringDefs, setScoringDefs] = useState([]);
  const [openConfirmScoringDelete, setOpenConfirmScoringDelete] = useState(false);
  const [openDialogScoring, setOpenDialogScoring] = useState(false);
  const [idScoring, setIdScoring] = useState(null);
  const [actionScoring, setActionScoring] = useState(null);
  const [nameScoring, setNameScoring] = useState('');
  const [typeScoring, setTypeScoring] = useState('credit');
  const [pointsScoring, setPointsScoring] = useState('');
  const [statusScoring, setStatusScoring] = useState('true');


  async function buscarConfigCrm(querys) {
    try {
      const result = await v2_actionConfigCrm(cliente._id, querys);
      if (result.status === 200) {
        if (querys) {
          console.log('setando data')
          setConfigCrm(result.data?.data);
        } else {
          setMotivos(result.data?.data?.motivos)
          setOrigens(
            (result.data?.data?.origens || []).filter(origem => origem.cliente_id)
          )
        }
      }
    } catch (error) {
      console.log(error);
    }
  }


  // Exporta os funis + estágios do cliente (apenas conteúdo portável: nome,
  // descrição e nomes/ordem dos estágios). Nenhum ID de cliente é incluído.
  function handleExportFunil() {
    const pipelines = (configCrm?.all_pipelines || []).map((p) => ({
      nome: p.pipeline_nome || p.nome || '',
      description: p.description || p.descricao || '',
      estagios: (p.estagios || []).map((e) => ({ nome: e.nome })),
    }));
    downloadJSON('funis.funil.json', buildEnvelope('lais-funil', pipelines));
  }

  // Recria os funis e estágios no cliente atual. Os novos cliente_id/criado_por
  // são injetados aqui (do `cliente`/`user` atuais).
  async function handleImportFunil(pipelines) {
    if (!Array.isArray(pipelines)) {
      throw new Error(t('funnel.core.importInvalidFile', 'Arquivo de funil inválido.'));
    }
    let funisCriados = 0;
    let estagiosCriados = 0;
    for (const p of pipelines) {
      const nome = p.nome || p.pipeline_nome;
      if (!nome) continue;
      const res = await v2_actionCriarPipeline(cliente._id, {
        scope: { nome, descricao: p.description || p.descricao || '', criadoPor: user._id },
      });
      const novoId = res?.data?.data?._id;
      if (!novoId) continue;
      funisCriados++;
      for (const e of p.estagios || []) {
        if (!e?.nome) continue;
        await v2_actionCriarEstagioFunil(cliente._id, {
          scope: { nome: e.nome, pipeline_id: novoId, criado_por: user.id, cliente_id: cliente._id },
        });
        estagiosCriados++;
      }
    }
    buscarConfigCrm('?all=1');
    return {
      message: t('funnel.core.importSuccessMessage', '{{funis}} funil(is) e {{estagios}} estágio(s) importados.', {
        funis: funisCriados,
        estagios: estagiosCriados,
      }),
    };
  }

  async function handleEditReason() {
    try {
      let data = {
        scope: {
          name: nameReason
        }
      }
      await v2_actionEditCrmReason(idReason, data);
      buscarConfigCrm(null)
      setOpenDialogReason(false);
      setAction(null);
      setNameReason('');
    } catch (error) {
      setOpenConfirmReasonDelete(false);
      console.log(error);
    }
  }
  async function handleDelete() {
    try {
      let data = {
        scope: {
          deleted: true
        }
      }
      await v2_actionEditCrmReason(idReason, data);
      buscarConfigCrm(null);
      setOpenConfirmReasonDelete(false);
      setAction(null);
      setNameReason('');
    } catch (error) {
      setOpenConfirmReasonDelete(false);
      console.log(error);
    }
  }

  async function handleDeletePipeline() {
    try {
      let data = {
        scope: {
          deleted: true
        }
      }
      await v2_actionDeletarPipeline(pipelineId);
      let querys = `?all=1`
      buscarConfigCrm(querys)
      setOpenModalConfirmacaoExclusao(false);
      setAction(null);
      setPipelineId(null);
    } catch (error) {
      setOpenModalConfirmacaoExclusao(false);
      console.log(error);
    }
  }

  async function handleCreateReasonLost() {
    try {
      let data = {
        name: nameReason
      }
      await v2_actionAddCrmReason(cliente._id, data);
      buscarConfigCrm(null);
      setOpenDialogReason(false);
      setNameReason('');
    } catch (error) {
      console.log(error);
    }
  }

  // Funções para gerenciar origens de contato
  async function handleCreateOrigin() {
    try {
      let data = {
        scope: {
          name: nameOrigin,
          description: descricaoOrigin,
          active: ativoOrigin
        }
      }
      await v2_actionAddCrmSource(cliente._id, data);
      buscarConfigCrm(null);
      setOpenDialogOrigin(false);
      setNameOrigin('');
      setDescricaoOrigin('');
      setAtivoOrigin(true);
    } catch (error) {
      console.log(error);
    }
  }

  async function handleEditOrigin() {
    try {
      let data = {
        scope: {
          name: nameOrigin,
          description: descricaoOrigin,
          active: ativoOrigin
        }
      }
      await v2_actionEditarCrmSource(idOrigin, data);
      buscarConfigCrm(null);
      setOpenDialogOrigin(false);
      setActionOrigin(null);
      setNameOrigin('');
      setDescricaoOrigin('');
      setAtivoOrigin(true);
    } catch (error) {
      console.log(error);
    }
  }

  async function handleDeleteOrigin() {
    try {
      await v2_actionDeletarCrmSource(idOrigin);
      buscarConfigCrm(null);
      setOpenConfirmOriginDelete(false);
      setActionOrigin(null);
      setNameOrigin('');
      setDescricaoOrigin('');
      setAtivoOrigin(true);
    } catch (error) {
      setOpenConfirmOriginDelete(false);
      console.log(error);
    }
  }

  // Scoring CRUD
  async function buscarScoringDefs() {
    try {
      const result = await v2_actionListScoringDefinitions(cliente._id);
      if (result.status === 200) {
        setScoringDefs(result.data?.data || []);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function handleCreateScoring() {
    try {
      let data = {
        name: nameScoring,
        type: typeScoring,
        points: Number(pointsScoring),
        status: statusScoring
      }
      await v2_actionAddScoringDefinition(cliente._id, data);
      buscarScoringDefs();
      setOpenDialogScoring(false);
      resetScoringFields();
    } catch (error) {
      console.log(error);
    }
  }

  async function handleEditScoring() {
    try {
      let data = {
        scope: {
          name: nameScoring,
          type: typeScoring,
          points: Number(pointsScoring),
          status: statusScoring
        }
      }
      await v2_actionEditScoringDefinition(idScoring, data);
      buscarScoringDefs();
      setOpenDialogScoring(false);
      resetScoringFields();
    } catch (error) {
      console.log(error);
    }
  }

  async function handleDeleteScoring() {
    try {
      await v2_actionDeleteScoringDefinition(idScoring);
      buscarScoringDefs();
      setOpenConfirmScoringDelete(false);
      resetScoringFields();
    } catch (error) {
      setOpenConfirmScoringDelete(false);
      console.log(error);
    }
  }

  function resetScoringFields() {
    setActionScoring(null);
    setNameScoring('');
    setTypeScoring('credit');
    setPointsScoring('');
    setStatusScoring('true');
    setIdScoring(null);
  }

  useEffect(() => {
    let querys = `?all=1`
    buscarConfigCrm(querys)
    buscarConfigCrm(null)
    buscarScoringDefs()
  }, [])

  return (
    <>
      <ModalAlerta
        open={modalAlerta}
        onClose={() => setModalAlerta(false)}
        mensagem={t('funnel.core.alerts.quickReplyFileAudio', 'Não é possivel editar resposta rapida do tipo arquivo ou audio, exclua e crie uma nova resposta rápida')}
      />

      <ModalConfimacao
        open={openModalConfirmacaoExclusao}
        onClose={() => setOpenModalConfirmacaoExclusao(false)}
        onConfirm={() => handleDeletePipeline()}
        loadingFinalizar={null}
        mensagem={t('funnel.core.confirmDeletePipeline', 'Deseja mesmo excluir esse funil?')}
      />

      <ModalConfimacao
        open={openConfirmReasonDelete}
        onClose={() => {
          setOpenConfirmReasonDelete(false);
        }}
        onConfirm={handleDelete}
        loadingFinalizar={loading}
        mensagem={t('funnel.reasons.confirmDelete', 'Deseja mesmo excluir?')}
      />

      <DrawerNovoFunil
        open={openDrawerNovoFunil}
        onClose={() => {
          setOpenDrawerNovoFunil(false);
          setIsEdit(false)
        }}
        onCallback={() => {
          let querys = `?all=1`
          buscarConfigCrm(querys)
          setOpenDrawerNovoFunil(false)
        }}
        user={user}
        cliente={cliente}
        isEdit={isEdit}
        pipeline={pipeline}
        pipelineId={pipeline?._id}
      />

      <ModalReasons
        open={openDialogReason}
        action={action}
        nome={nameReason}
        changeNome={(e) => {
          setNameReason(e.target.value)
        }}
        onSubmit={action === "Criar" ? handleCreateReasonLost : handleEditReason}
        onClose={() => {
          setOpenDialogReason(false);
          setNameReason('');
          setIdReason(null);
        }}
      />

      <ModalConfimacao
        open={openConfirmOriginDelete}
        onClose={() => {
          setOpenConfirmOriginDelete(false);
        }}
        onConfirm={handleDeleteOrigin}
        loadingFinalizar={loading}
        mensagem={t('funnel.origins.confirmDelete', 'Deseja mesmo excluir esta origem de contato?')}
      />

      <ModalOrigins
        open={openDialogOrigin}
        action={actionOrigin}
        nome={nameOrigin}
        descricao={descricaoOrigin}
        ativo={ativoOrigin}
        changeNome={(e) => {
          setNameOrigin(e.target.value)
        }}
        changeDescricao={(e) => {
          setDescricaoOrigin(e.target.value)
        }}
        changeAtivo={(checked) => {
          setAtivoOrigin(checked)
        }}
        onSubmit={actionOrigin === "Criar" ? handleCreateOrigin : handleEditOrigin}
        onClose={() => {
          setOpenDialogOrigin(false);
          setNameOrigin('');
          setDescricaoOrigin('');
          setAtivoOrigin(true);
          setIdOrigin(null);
        }}
      />

      <ModalConfimacao
        open={openConfirmScoringDelete}
        onClose={() => {
          setOpenConfirmScoringDelete(false);
        }}
        onConfirm={handleDeleteScoring}
        loadingFinalizar={loading}
        mensagem={t('funnel.core.score.confirmDelete', 'Deseja mesmo excluir esta regra de score?')}
      />

      <ModalScoring
        open={openDialogScoring}
        action={actionScoring}
        nome={nameScoring}
        changeNome={(e) => setNameScoring(e.target.value)}
        tipo={typeScoring}
        changeTipo={(value) => setTypeScoring(value)}
        pontos={pointsScoring}
        changePontos={(e) => setPointsScoring(e.target.value)}
        status={statusScoring}
        changeStatus={(value) => setStatusScoring(value)}
        onSubmit={actionScoring === "Criar" ? handleCreateScoring : handleEditScoring}
        onClose={() => {
          setOpenDialogScoring(false);
          resetScoringFields();
        }}
      />

      <ModalImportarJSON
        open={openModalImportarFunil}
        onClose={() => setOpenModalImportarFunil(false)}
        expectedKind="lais-funil"
        title={t('funnel.core.importModal.title', 'Importar Funis')}
        description={t('funnel.core.importModal.description', 'Selecione um arquivo .funil.json exportado. Os funis e estágios serão recriados no cliente atual (apenas nomes e ordem — sem vínculos do cliente de origem).')}
        onImport={handleImportFunil}
      />

      <div className={D.root} style={{ fontFamily: D.fontStack }}>
        {/* `?tab=` torna as abas deep-linkáveis: telas que dependem destes cadastros
            (ex.: os selects vazios do card de negócio) mandam o usuário direto para
            a aba certa em vez de largá-lo no Kanban tendo que procurar. Lido só na
            montagem — a navegação por clique continua igual. */}
        <Tabs defaultValue={TABS_VALIDAS.includes(tabInicial) ? tabInicial : 'kanban'} className="w-full">
          <div className={D.stickyHeader}>
            <div className={D.container}>
              <div className={D.topbar}>
                <div className={D.topbarIdentity}>
                  <div className="min-w-0">
                    <h1 className={D.title}>{t('funnel.core.header.title', 'Funil')}</h1>
                    <p className={D.subtitle}>
                      {t('funnel.core.header.subtitle', 'Gerencie os funis de vendas da sua empresa. Configure pipelines, etapas e status.')}
                    </p>
                  </div>
                </div>
                <div className={D.topbarActions}>
                  <button type="button" onClick={handleExportFunil} className={D.ghostButton}>
                    {t('buttons.export', { ns: 'common' })}
                  </button>
                  <button type="button" onClick={() => setOpenModalImportarFunil(true)} className={D.ghostButton}>
                    {t('buttons.import', { ns: 'common' })}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOpenDrawerNovoFunil(true);
                      setAction('Criar');
                    }}
                    className={D.primaryButton}
                  >
                    {t('funnel.core.header.createButton', 'Criar Funil')}
                  </button>
                </div>
              </div>

              <TabsList className={D.tabsList}>
                <TabsTrigger className={D.tab} value="kanban">Kanban</TabsTrigger>
                <TabsTrigger className={D.tab} value="perdas">{t('funnel.core.tabs.reasons', 'Motivos de perda')}</TabsTrigger>
                <TabsTrigger className={D.tab} value="origens">{t('funnel.core.tabs.origins', 'Origens de Contato')}</TabsTrigger>
                <TabsTrigger className={D.tab} value="lead-tags">{t('funnel.core.tabs.leadTags', 'Tags de Negociação')}</TabsTrigger>
                <TabsTrigger className={D.tab} value="lead-status">{t('funnel.core.tabs.leadStatus', 'Status de Venda')}</TabsTrigger>
                <TabsTrigger className={D.tab} value="campos">{t('funnel.core.tabs.customFields', 'Campos Personalizados')}</TabsTrigger>
                <TabsTrigger className={D.tab} value="layout">{t('funnel.core.tabs.layout', 'Layout do Card')}</TabsTrigger>
              </TabsList>
            </div>
          </div>

          <div className={`${D.container} ${D.tabsBody} ${D.pageBody}`}>
            <TabsContent value="kanban" className="mt-0">
              <div className={D.sectionHeader}>
                <div className={D.sectionHeaderMain}>
                  <h2 className={D.sectionHeaderTitle}>{t('funnel.core.tabs.kanban', 'Kanban')}</h2>
                  <p className={D.sectionHeaderSub}>
                    {t('funnel.core.kanbanHint', 'Organize os funis e arraste os estágios para definir a ordem do pipeline.')}
                  </p>
                </div>
              </div>

              {!configCrm?.all_pipelines?.length ? (
                <div className={`${D.card} ${D.emptyState}`}>
                  <div className={D.emptyIcon}>
                    <Zap size={28} />
                  </div>
                  <h3 className={D.emptyTitle}>{t('funnel.core.emptyState.title', 'Nenhum funil cadastrado')}</h3>
                  <p className={D.emptyDesc}>
                    {t('funnel.core.emptyState.description', 'Você ainda não possui nenhum funil de vendas configurado.')}
                  </p>
                </div>
              ) : (
                <div className={D.pipelineGrid}>
                  {configCrm?.all_pipelines?.map((item, index) => (
                    <PipileneItem
                      key={item._id || index}
                      user={user}
                      onDelete={(item) => {
                        setPipelineId(item._id);
                        setOpenModalConfirmacaoExclusao(true);
                      }}
                      onEdit={(item) => {
                        setPipeline(item);
                        setOpenDrawerNovoFunil(true);
                        setIsEdit(true);
                      }}
                      cliente={cliente}
                      pipelines={configCrm?.all_pipelines}
                      index={index}
                      item={item}
                      onCallback={() => {
                        buscarConfigCrm('?all=1');
                      }}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="perdas" className="mt-0">
              <div className={D.sectionHeader}>
                <div className={D.sectionHeaderMain}>
                  <h2 className={D.sectionHeaderTitle}>{t('funnel.reasons.sectionTitle', 'Motivos de perda')}</h2>
                  <p className={D.sectionHeaderSub}>
                    {t('funnel.reasons.sectionHint', 'Motivos usados quando um negócio é marcado como perdido.')}
                  </p>
                </div>
                <button
                  type="button"
                  className={D.primaryButton}
                  onClick={() => {
                    setOpenDialogReason(true);
                    setAction('Criar');
                  }}
                >
                  {t('buttons.create', { ns: 'common' })}
                </button>
              </div>

              <div className={D.cardFlush}>
                {motivos?.length === 0 ? (
                  <div className={D.emptyState}>
                    <p className={D.emptyDesc}>{t('funnel.reasons.emptyState', 'Nenhum motivo foi cadastrado')}</p>
                  </div>
                ) : (
                  <div className={D.tableWrap}>
                    <TableReasons
                      data={motivos}
                      onSet={(value) => setIdReason(value)}
                      onConfirm={(value) => handleDelete(value)}
                      onDelete={(row) => {
                        setIdReason(row._id);
                        setOpenConfirmReasonDelete(true);
                      }}
                      onEdit={(row) => {
                        setOpenDialogReason(true);
                        setAction('Editar');
                        setIdReason(row._id);
                        setNameReason(row.name);
                      }}
                    />
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="origens" className="mt-0">
              <div className={D.sectionHeader}>
                <div className={D.sectionHeaderMain}>
                  <h2 className={D.sectionHeaderTitle}>{t('funnel.origins.sectionTitle', 'Origens de Contato')}</h2>
                  <p className={D.sectionHeaderSub}>
                    {t('funnel.origins.sectionHint', 'De onde veio o lead — site, indicação, anúncio e outros.')}
                  </p>
                </div>
                <button
                  type="button"
                  className={D.primaryButton}
                  onClick={() => {
                    setOpenDialogOrigin(true);
                    setActionOrigin('Criar');
                  }}
                >
                  {t('buttons.create', { ns: 'common' })}
                </button>
              </div>

              <div className={D.cardFlush}>
                {origens?.length === 0 ? (
                  <div className={D.emptyState}>
                    <p className={D.emptyDesc}>{t('funnel.origins.emptyState', 'Nenhuma origem foi cadastrada')}</p>
                  </div>
                ) : (
                  <div className={D.tableWrap}>
                    <TableOrigins
                      data={origens}
                      onEdit={(row) => {
                        setOpenDialogOrigin(true);
                        setActionOrigin('Editar');
                        setIdOrigin(row._id);
                        setNameOrigin(row.name);
                        setDescricaoOrigin(row.description);
                        setAtivoOrigin(row.active);
                      }}
                      onDelete={(row) => {
                        setIdOrigin(row._id);
                        setOpenConfirmOriginDelete(true);
                      }}
                    />
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="lead-tags" className="mt-0">
              <TabLeadTags cliente={cliente} usuarioId={user?.id} />
            </TabsContent>

            <TabsContent value="lead-status" className="mt-0">
              <TabLeadStatus cliente={cliente} usuarioId={user?.id} />
            </TabsContent>

            <TabsContent value="score" className="mt-0">
              <div className={D.sectionHeader}>
                <div className={D.sectionHeaderMain}>
                  <h2 className={D.sectionHeaderTitle}>{t('funnel.core.score.sectionTitle', 'Regras de Score')}</h2>
                  <p className={D.sectionHeaderSub}>
                    {t('funnel.core.score.sectionHint', 'Pontue ações do lead para priorizar o que merece atenção.')}
                  </p>
                </div>
                <button
                  type="button"
                  className={D.primaryButton}
                  onClick={() => {
                    setOpenDialogScoring(true);
                    setActionScoring('Criar');
                  }}
                >
                  {t('buttons.create', { ns: 'common' })}
                </button>
              </div>

              <div className={D.cardFlush}>
                {scoringDefs?.length === 0 ? (
                  <div className={D.emptyState}>
                    <p className={D.emptyDesc}>{t('funnel.core.score.emptyState', 'Nenhuma regra de score foi cadastrada')}</p>
                  </div>
                ) : (
                  <div className={D.tableWrap}>
                    <TableScoring
                      data={scoringDefs}
                      onEdit={(row) => {
                        setOpenDialogScoring(true);
                        setActionScoring('Editar');
                        setIdScoring(row._id);
                        setNameScoring(row.name);
                        setTypeScoring(row.type);
                        setPointsScoring(row.points);
                        setStatusScoring(row.status);
                      }}
                      onDelete={(row) => {
                        setIdScoring(row._id);
                        setOpenConfirmScoringDelete(true);
                      }}
                    />
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="campos" className="mt-0">
              <TabCustomFields
                cliente={cliente}
                pipelines={configCrm?.all_pipelines || []}
              />
            </TabsContent>

            <TabsContent value="layout" className="mt-0">
              <TabLayoutConfig cliente={cliente} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </>
  );
}
